import useOverlay from "@/hooks/common/useOverlay";
import {scrollRestore} from "@/hooks/common/useScrollRestoration";
import {useTaggingLoad} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Footer from "@COMPONENTS/layout/footer/Mobile";
import Header from "@COMPONENTS/layout/header/Mobile";
import EventListPopup from "@COMPONENTS/product/common/popup/mobile/EventListPopup";
import TaggingGTM from "@COMPONENTS/product/pages/localList/common/GTMTags";
import useFilterStore from "@COMPONENTS/product/pages/localList/common/filterHook";
import {
	LocalFilterBar,
	LocalFilterSummary,
	LocalLvl4List,
} from "@COMPONENTS/product/pages/localList/mobile/filter/LocalHeadFilter";
import LocalGoodsList from "@COMPONENTS/product/pages/localList/mobile/goodsList/LocalGoodsList";
import * as style from "@COMPONENTS/style/productlist/productlistM";
import getSEO from "@FRAMEWORK/utils/seo";
import useGlobalGoodsFlag from "@QUERIES/bff/global/useGlobalGoodsFlag";
import useGoodsFilter, {GoodsFilter, goodsFilterSSR} from "@QUERIES/prdt/goods/useGoodsFilter";
import {useAppDispatch, useAppSelector} from "@STORE/index";
import {initGoodsFilter, setGoodsFilter} from "@STORE/store-filter";
import dayjs from "dayjs";
import {useRouter} from "next/router";
import {useEffect, useRef, useState} from "react";

const Page = ({filterData}: {filterData: GoodsFilter | null}) => {
	const router = useRouter();
	const dspSid = router.query.dspSid as string;
	const goodsCd = router.query.goodsCd as string;
	const goodsMonth = router.query?.goodsMonth as string;
	const goodsAirCd = router.query?.goodsAirCd as string;
	const {data, isError} = useGoodsFilter(dspSid as string, {
		placeholderData: {body: filterData},
	});
	if (isError) {
		return <p>에러가 발생했습니다.</p>;
	}
	const filter = data?.body;

	// goodsFlag 미리 호출 (FlagPromotion 에서 사용)
	useGlobalGoodsFlag();

	const {getFilteringProduct, setFilter} = useFilterStore();

	/* filterStore 초기화 */
	const filterStore = useAppSelector(state => state.goodsFilterStore);
	const dispatch = useAppDispatch();
	useEffect(() => {
		const storeDspSid = filterStore.goodsFilter ? Object.keys(filterStore?.goodsFilter)[0] : {};
		if (dspSid && dspSid !== storeDspSid) {
			const storeInit = {[dspSid]: [{text: "추천순", value: "sortOrder", key: "sort", asc: true}]};
			dispatch(initGoodsFilter()); /* 데이터 초기화*/
			dispatch(setGoodsFilter(storeInit)); /* dspSid 세팅 */
		}
	}, [router]);

	/** queryString에 goodsAirCd 이용 교통 세팅 */
	useEffect(() => {
		if (!goodsAirCd || !filter?.airList) return;
		const airListData = filter.airList?.replaceAll("|", "").split("^") ?? [];
		const airList = [];
		for (let i = 0; i < airListData.length; i += 2) {
			const code = airListData[i];
			const name = airListData[i + 1];
			airList.push({code, name});
		}

		const arrAirCd = goodsAirCd.split(",");
		const setArr = JSON.parse(JSON.stringify(filterStore.goodsFilter[dspSid])) || [];
		const newItems = arrAirCd.reduce((acc, value) => {
			const text = airList.find(item => item.code === value)?.name;
			if (text) acc.push({key: "airCd", value, text});
			return acc;
		}, []);

		// 최종 배열: 기존 필터 + 새로 추가된 항공사 필터
		const merged = [...setArr, ...newItems];
		// 중복 제거
		const deduped = merged.filter(
			(item, index, self) =>
				item.key !== "airCd" || self.findIndex(v => v.key === "airCd" && v.value === item.value) === index,
		);
		setFilter(deduped);
	}, [goodsAirCd, filter?.airList]);

	// header scroll
	const FilterRef = useRef<HTMLDivElement>();
	const [direction, setDirection] = useState(false);
	let lastScroll = 0;

	const controlDirection = () => {
		if (window.scrollY > lastScroll) {
			setDirection(false);
		} else {
			setDirection(true);
		}

		lastScroll = window.scrollY;
	};

	const [viewProduct] = useOverlay();
	const productList = getFilteringProduct();
	const hasRouterGoods = productList.some(v => v.goodsCd === goodsCd);

	/** scrollRestore 상품 클릭 위치 저장 */
	const {getRestoreData} = scrollRestore();
	const restoreData = getRestoreData() || {top: 0, goodsCd: null, startMonth: null};
	const hasRestoreGoodsCd = productList.some(v => v.goodsCd === restoreData.goodsCd);

	useEffect(() => {
		// 상품 클릭 위치 저장 실행
		if (hasRestoreGoodsCd)
			viewProduct(props => (
				<EventListPopup {...props} goodsCd={restoreData.goodsCd} startMonth={restoreData.startMonth} />
			));
		// Url에 goodsCd가 존재할 경우 그리고 goodsCd가 product list에 존재할 경우만 팝업
		else if (goodsCd && hasRouterGoods)
			viewProduct(props => (
				<EventListPopup
					{...props}
					goodsCd={goodsCd}
					startMonth={goodsMonth && dayjs(goodsMonth).isValid() && goodsMonth}
				/>
			));
	}, [hasRouterGoods, hasRestoreGoodsCd, goodsMonth]);

	/* 상품 클릭 위치 스크롤 위치 이동 */
	useEffect(() => {
		if (!getRestoreData()) return;
		scrollTo({top: restoreData.top});
	}, []);

	useEffect(() => {
		// 여행 포인트
		window.addEventListener("scroll", controlDirection);
		return () => {
			window.removeEventListener("scroll", controlDirection);
		};
	}, []);

	if (!filter) return <></>;

	useTaggingLoad("pkg_view_goodsCdList");

	return (
		<>
			<style.Wrap>
				{/* Header */}
				<Header category />
				{/* 상단 필터 */}
				<LocalLvl4List listStr={filter.lvl4List} />
				<LocalFilterBar ref={FilterRef} />
				<LocalFilterSummary isScroll={direction} />
				{/* 상품 전시 리스트 */}
				<LocalGoodsList />
				{/* Footer */}
				<Footer />
			</style.Wrap>
			{router.query?.goodsCd && (
				<ErrorBoundary>
					<TaggingGTM menu={(router.query?.menu as string) ?? ""} goodsCd={router.query?.goodsCd as string} />
				</ErrorBoundary>
			)}
		</>
	);
};

export default Page;

export async function getServerSideProps({query}) {
	try {
		const {dspSid = "", goodsCd = ""} = query;
		const seo = await getSEO(dspSid, "goodsCd", goodsCd);

		const result = await goodsFilterSSR(query.dspSid);
		if (!(result.status === 200 || result.status === 304)) {
			throw new Error("데이터를 찾을 수 없습니다.");
		}
		return {
			props: {
				filterData: result.data,
				...seo,
			},
		};
	} catch (e) {
		return {
			props: {filterData: {}, seoMenu: {}, seo: {}},
		};
	}
}
